/**
 * Scalar field display helpers for field-display-renderer.
 * Handles all non-relation field types (date, boolean, select, tags, multi-user, json, text, etc.)
 * Mirrors the pattern in cell-rendering-scalars.tsx for the cell-value-rendering pipeline.
 */

import React from "react";
import { Badge } from "@/components/ui/badge";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { JsonFieldDisplay } from "@/components/shared/JsonFieldDisplay";
import { LinkifiedText } from "@west-tennessee-consulting/link-preview/react";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";
import { normalizeMultiUserData } from "./field-helpers";
import { engagementStalenessBadgeClassNames } from "./cell-value-rendering-shared";
import { cn } from "@/lib/utils";
import type { SimpleFieldConfig, FieldValue } from "../types";
import type { JsonFieldValue } from "@/components/shared/types";

/**
 * Renders date/datetime fields, with optional engagement-staleness badge.
 */
export function renderFieldDisplayDatetime(
  value: FieldValue,
  fieldConfig: SimpleFieldConfig,
  className: string
): React.ReactNode {
  if (fieldConfig.engagementStaleness) {
    if (value === null || value === undefined) {
      return (
        <Badge
          variant="secondary"
          className={cn(
            "text-xs font-normal border-0",
            "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
          )}
        >
          Never
        </Badge>
      );
    }
    const d = new Date(value as string);
    if (Number.isNaN(d.getTime())) {
      return <div className={`text-sm ${className}`}>-</div>;
    }
    const daysSince = (Date.now() - d.getTime()) / 86_400_000;
    return (
      <Badge
        variant="secondary"
        className={cn("text-xs font-normal border-0", engagementStalenessBadgeClassNames(daysSince))}
      >
        {formatDate(value as string)}
      </Badge>
    );
  }
  return <div className={`text-sm ${className}`}>{formatDate(value as string)}</div>;
}

/**
 * Renders a time field (strips date portion if present).
 */
export function renderFieldDisplayTime(value: FieldValue, className: string): React.ReactNode {
  if (value && typeof value === "string") {
    const timeValue = value.includes("T") ? value.split("T")[1] : value;
    return <div className={`text-sm ${className}`}>{timeValue.substring(0, 5)}</div>;
  }
  return <div className={`text-sm ${className}`}>{String(value)}</div>;
}

/**
 * Renders a boolean field as a Yes/No badge.
 */
export function renderFieldDisplayBoolean(value: FieldValue, className: string): React.ReactNode {
  return (
    <Badge variant={value ? "default" : "secondary"} className={className}>
      {value ? "Yes" : "No"}
    </Badge>
  );
}

/**
 * Renders a select field using its configured option label.
 */
export function renderFieldDisplaySelect(
  value: FieldValue,
  fieldConfig: SimpleFieldConfig,
  className: string
): React.ReactNode {
  const option = fieldConfig.options?.find((opt) => opt.value === value);
  return <div className={`text-sm ${className}`}>{option?.label || String(value)}</div>;
}

/**
 * Renders a tags array as a row of outline badges.
 */
export function renderFieldDisplayTags(value: FieldValue, className: string): React.ReactNode {
  if (!Array.isArray(value)) return null;
  if (value.length === 0) {
    return (
      <div className={`text-sm text-muted-foreground italic ${className}`}>No tags</div>
    );
  }
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {value.map((tag: any, index: number) => (
        <Badge key={index} variant="outline" className="text-xs">
          {String(tag)}
        </Badge>
      ))}
    </div>
  );
}

/**
 * Renders a multi-user array as a row of SmartUserDisplay chips (max 3 + overflow count).
 */
export function renderFieldDisplayMultiUser(value: FieldValue, className: string): React.ReactNode {
  if (!Array.isArray(value)) return null;
  const normalizedValue = normalizeMultiUserData(value);
  if (normalizedValue.length === 0) {
    return (
      <div className={`text-sm text-muted-foreground italic ${className}`}>No users assigned</div>
    );
  }
  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {normalizedValue.slice(0, 3).map((userId: string) => (
        <SmartUserDisplay
          key={userId}
          userId={userId}
          showIcon={false}
          className="text-xs"
          showPopover={false}
        />
      ))}
      {normalizedValue.length > 3 && (
        <span className="text-xs text-muted-foreground">+{normalizedValue.length - 3} more</span>
      )}
    </div>
  );
}

/**
 * Renders a JSON field via JsonFieldDisplay.
 */
export function renderFieldDisplayJson(
  value: FieldValue,
  fieldId: string,
  className: string
): React.ReactNode {
  return (
    <JsonFieldDisplay
      value={value as JsonFieldValue}
      fieldId={fieldId}
      maxItems={3}
      className={className}
    />
  );
}

/**
 * Renders text/textarea fields, including financial formatting for cost/rate/amount fields.
 */
export function renderFieldDisplayText(
  value: FieldValue,
  fieldId: string,
  fieldConfig: SimpleFieldConfig,
  className: string
): React.ReactNode {
  // Financial fields
  if (
    fieldId.includes("cost") ||
    fieldId.includes("billed") ||
    fieldId.includes("rate") ||
    fieldId.includes("amount")
  ) {
    const numValue = Number(value);
    if (!isNaN(numValue)) {
      return <div className={`text-sm font-medium ${className}`}>${numValue.toFixed(2)}</div>;
    }
  }

  const stringValue = String(value);
  const isTextareaField =
    fieldConfig.type === "textarea" ||
    fieldId === "body" ||
    fieldId?.includes("description") ||
    fieldId?.includes("notes") ||
    fieldId?.includes("content");

  if (isTextareaField) {
    return <LinkifiedText text={stringValue} className={`text-sm break-words ${className}`} />;
  }

  if (stringValue.length > 100) {
    return <div className={`text-sm ${className}`}>{stringValue.substring(0, 100)}...</div>;
  }

  return <div className={`text-sm ${className}`}>{stringValue}</div>;
}

/**
 * Dispatches scalar field rendering by fieldConfig.type.
 * Returns null for "custom" email_aliases (caller handles), undefined when type is unrecognized.
 */
export function renderFieldDisplayScalar(
  value: FieldValue,
  fieldId: string,
  fieldConfig: SimpleFieldConfig,
  className: string
): React.ReactNode | null | undefined {
  const fieldType = fieldConfig?.type || "text";

  // Company technical_identifiers is an object array rendered by its bespoke
  // editor on every surface (table cell + detail). Return null so no code path
  // ever String()s the array into "[object Object]" — the caller mounts the
  // TechnicalIdentifiersEditor (same contract as email_aliases/phone_aliases).
  if (fieldId === "technical_identifiers") return null;

  switch (fieldType) {
    case "date":
    case "datetime":
      return renderFieldDisplayDatetime(value, fieldConfig, className);

    case "time":
      return renderFieldDisplayTime(value, className);

    case "boolean":
      return renderFieldDisplayBoolean(value, className);

    case "select":
      return renderFieldDisplaySelect(value, fieldConfig, className);

    case "tags": {
      const node = renderFieldDisplayTags(value, className);
      if (node !== null) return node;
      break;
    }

    case "multi-user": {
      const node = renderFieldDisplayMultiUser(value, className);
      if (node !== null) return node;
      break;
    }

    case "custom":
      // email_aliases / phone_aliases: let SimpleTable handle rendering the component
      if (fieldId === "email_aliases") return null;
      if (fieldId === "phone_aliases") return null;
      break;

    case "json":
      return renderFieldDisplayJson(value, fieldId, className);

    case "textarea":
    case "text":
    default:
      return renderFieldDisplayText(value, fieldId, fieldConfig, className);
  }

  // Fallback for unhandled array/object values
  return undefined;
}
